import * as React from "react"
import { cn } from "@/lib/utils"

export interface ViewCollectionSvgProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon in pixels
   * @default 18
   */
  size?: number
}

/**
 * View Collection icon as SVG
 *
 * This icon represents viewing a collection, using an eye symbol.
 * Used for navigation to Trophy Room/collection view.
 * Simple design optimized for small sizes (w-4 h-4).
 *
 * @example
 * ```tsx
 * <ViewCollectionSvg />
 * <ViewCollectionSvg size={24} className="text-black" />
 * <ViewCollectionSvg className="w-4 h-4" />
 * ```
 */
export const ViewCollectionSvg = React.forwardRef<SVGSVGElement, ViewCollectionSvgProps>(
  ({ size = 18, className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn("inline-flex shrink-0", className)}
        aria-hidden="true"
        {...props}
      >
        {/* Eye outline */}
        <path
          d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Outer iris circle */}
        <circle
          cx="12"
          cy="12"
          r="4"
          stroke="currentColor"
          strokeWidth="2"
        />
        {/* Inner pupil */}
        <circle
          cx="12"
          cy="12"
          r="1.5"
          fill="currentColor"
        />
      </svg>
    )
  }
)

ViewCollectionSvg.displayName = "ViewCollectionSvg"
